import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import FilterPlate from '..';
import { VALUE_TYPE } from '../constants';
import FilterPlateCEnum from './FilterPlateCEnum';
import FilterPlateCBoolean from './FilterPlateCBoolean';
import FilterPlateCString from './FilterPlateCString';
import FilterPlateCDate from './FilterPlateCDate';
import { useFilters } from 'src/hooks/filters/useFilters';
import { useSort } from 'src/hooks/filters/useSort';

const FilterPlateCDefault = ({
	code,
	valueType,
	payload,
	onComplete,
	isLoading,
	...rest
}) => {
	const { setFilters, transformFilterValue, removeFilter, getFilter } =
		useFilters();
	const { setSort, getSort } = useSort();
	const currentFilter = useMemo(() => getFilter(code), [getFilter, code]);
	const currentSort = useMemo(() => getSort(), [getSort]);
	const sortDirection =
		currentSort?.field === code ? currentSort.direction : undefined;
	const currentOp = currentFilter ? Object.keys(currentFilter)[0] : null;
	const currentValue = useMemo(() => {
		if (!currentFilter) return '';

		return Object.values(currentFilter)[0];
	}, [currentFilter]);

	const handleSubmit = useCallback(
		(data) => {
			let op = data.option.value;
			const value = transformFilterValue(data, code, op);

			if (op === 'isNotNull') op = 'isNull';
			if (op === 'yes' || op === 'no') op = 'eq';

			// Mark applying first so popover survives URL/search-param update
			if (onComplete) onComplete();

			setFilters(code, op, value);
		},
		[onComplete, code, setFilters, transformFilterValue],
	);

	const handleCancel = useCallback(() => {
		if (onComplete) onComplete();

		removeFilter(code);
	}, [onComplete, code, removeFilter]);

	const handleSort = useCallback(
		(direction) => {
			if (onComplete) onComplete();

			setSort(code, direction);
		},
		[onComplete, code, setSort],
	);

	const commonProps = {
		onSubmit: handleSubmit,
		onSort: handleSort,
		onCancel: handleCancel,
		sortDirection,
		initialSelected: currentValue,
		operator: currentOp,
		isLoading,
	};

	switch (valueType) {
		case VALUE_TYPE.date:
			return <FilterPlateCDate {...commonProps} {...payload} />;
		case VALUE_TYPE.string:
			return <FilterPlateCString {...commonProps} {...payload} />;
		case VALUE_TYPE.enum:
			return <FilterPlateCEnum {...commonProps} {...payload} />;
		case VALUE_TYPE.number:
			return (
				<FilterPlateCString isNumber {...commonProps} {...payload} />
			);
		case VALUE_TYPE.boolean:
			return <FilterPlateCBoolean {...commonProps} {...payload} />;
		default:
			return <FilterPlate {...commonProps} {...rest} />;
	}
};

FilterPlateCDefault.propTypes = {
	...FilterPlate.propTypes,
	code: PropTypes.string,
	type: PropTypes.string,
	payload: PropTypes.oneOfType([PropTypes.shape(FilterPlateCEnum.propTypes)]),
	onComplete: PropTypes.func,
	isLoading: PropTypes.bool,
};

export default FilterPlateCDefault;
